import React, { useState, useEffect } from 'react';
import { Navbar, Nav, Container, Offcanvas } from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHome,
  faNewspaper,
  faBars,
  faTimes,
  faLanguage
} from '@fortawesome/free-solid-svg-icons';

const Header = () => {
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [isArabic, setIsArabic] = useState(true);

  // Load custom Arabic fonts
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Black.ttf') format('truetype');
        font-weight: 900;
        font-style: normal;
      }
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Bold.ttf') format('truetype');
        font-weight: 700;
        font-style: normal;
      }
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Medium.ttf') format('truetype');
        font-weight: 500;
        font-style: normal;
      }
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Regular.ttf') format('truetype');
        font-weight: 400;
        font-style: normal;
      }
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Light.ttf') format('truetype');
        font-weight: 300;
        font-style: normal;
      }
    `;
    document.head.appendChild(style);
  }, []);

  // New Theme Colors based on the provided palette
  const theme = {
    darkEmerald: '#255853',      // Dark Emerald Teal (Primary Dark)
    emerald: '#357E77',          // Emerald Teal (Primary)
    goldenSand: '#D3BA80',       // Golden Sand (Primary Accent)
    lightGolden: '#E0CFA6',      // Light Golden Sand (Light Accent)
    charcoal: '#32373B',         // Charcoal Gray (Text)
    darkGray: '#232729',         // Dark Charcoal
    pureWhite: '#FFFFFF',        // White
    lightGray: '#F0F0F0',        // Pure White Gray
    border: '#E0CFA6',           // Light Golden Border
    hover: 'rgba(37, 88, 83, 0.1)' // Dark Emerald Teal with transparency
  };

  const getFontFamily = (weight = 400) => {
    return isArabic ? `'ITF Qomra Arabic', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif` : "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif";
  };

  // Translation dictionary
  const translations = {
    'Home': 'الرئيسية',
    'News': 'الأخبار',
    'Videos': 'الفيديوهات',
    'Gallery': 'المعرض',
    'Contact': 'اتصل بنا',
    'AL-IRDA MEDIA': 'مؤسسة إرضاء الإعلامية',
    'Switch Language': 'تبديل اللغة',
    'Official media platform of Al-Irda Initiative': 'المنصة الإعلامية الرسمية لمؤسسة إرضاء'
  };

  const translateText = (text) => {
    return isArabic ? (translations[text] || text) : text;
  };

  const handleClose = () => setShowOffcanvas(false);
  const handleShow = () => setShowOffcanvas(true);

  const toggleLanguage = () => {
    setIsArabic(!isArabic);
    // Optionally, you can also trigger language change globally
    // localStorage.setItem('preferredLanguage', !isArabic ? 'ar' : 'en');
  };

  return (
    <>
      <Navbar 
        bg="white" 
        expand="lg" 
        className="navbar-clean shadow-sm" 
        sticky="top"
        style={{ 
          borderBottom: `2px solid ${theme.goldenSand}`, 
          direction: isArabic ? 'rtl' : 'ltr',
          fontFamily: getFontFamily()
        }}
      >
        <Container>
          <div className="d-flex align-items-center justify-content-between w-100">
            {/* Brand Section */}
            <LinkContainer to="/" style={{ flex: '0 0 auto' }}>
              <Navbar.Brand className="d-flex align-items-center" style={{ margin: 0, padding: 0 }}>
                <img 
                  src="/al-majd.png" 
                  alt="مؤسسة إرضاء الإعلامية" 
                  style={{
                    width: '55px',
                    height: '55px',
                    objectFit: 'contain',
                    marginRight: isArabic ? '-30px' : '0',
                    marginLeft: isArabic ? '0' : '-10px'
                  }}
                />
                <div className="d-flex flex-column" style={{ 
                  margin: 0,
                  alignItems: isArabic ? 'flex-end' : 'flex-start'
                }}>
                  <span 
                    className="navbar-brand-text"
                    style={{
                      fontSize: '1.4rem',
                      fontWeight: '700',
                      color: theme.darkEmerald,
                      lineHeight: '1.2',
                      margin: 0,
                      fontFamily: getFontFamily(700)
                    }}
                  >
                    {translateText('AL-IRDA MEDIA')}
                  </span>
                  <small 
                    className="brand-subtitle"
                    style={{
                      fontSize: '0.7rem',
                      color: theme.emerald,
                      fontWeight: '500',
                      marginTop: '2px',
                      opacity: '0.9',
                      letterSpacing: isArabic ? '0' : '1px',
                      fontFamily: getFontFamily(500)
                    }}
                  >
                    {translateText('Official media platform of Al-Irda Initiative')}
                  </small>
                </div>
              </Navbar.Brand>
            </LinkContainer>
            
            {/* Desktop Navigation */}
            <Navbar.Collapse id="main-nav" className="justify-content-center flex-grow-1">
              <Nav className="mx-auto" style={{ gap: '0.5rem' }}>
                <LinkContainer to="/">
                  <Nav.Link 
                    className="nav-link-clean"
                    style={{
                      color: `${theme.darkEmerald} !important`,
                      fontWeight: '500',
                      padding: '0.5rem 1rem',
                      borderRadius: '8px',
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap',
                      fontFamily: getFontFamily(500)
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = theme.hover;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                    }}
                  >
                    <FontAwesomeIcon icon={faHome} className={isArabic ? "ms-1" : "me-1"} />
                    {translateText('Home')}
                  </Nav.Link>
                </LinkContainer>

                <LinkContainer to="/news">
                  <Nav.Link 
                    className="nav-link-clean"
                    style={{
                      color: `${theme.darkEmerald} !important`,
                      fontWeight: '500',
                      padding: '0.5rem 1rem',
                      borderRadius: '8px',
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap',
                      fontFamily: getFontFamily(500)
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = theme.hover;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                    }}
                  >
                    <FontAwesomeIcon icon={faNewspaper} className={isArabic ? "ms-1" : "me-1"} />
                    {translateText('News')}
                  </Nav.Link>
                </LinkContainer>
              </Nav>
            </Navbar.Collapse>

            {/* Desktop Language Toggle */}
            <div className="d-none d-lg-flex align-items-center" style={{ flex: '0 0 auto' }}>
              <button 
                onClick={toggleLanguage}
                className="nav-link-clean"
                style={{
                  background: 'none',
                  border: `1px solid ${theme.darkEmerald}`,
                  color: `${theme.darkEmerald} !important`,
                  fontWeight: '500',
                  padding: '0.4rem 1rem',
                  borderRadius: '25px',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  fontFamily: getFontFamily(500),
                  fontSize: '0.9rem'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = theme.darkEmerald;
                  e.target.style.color = 'white !important';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = `${theme.darkEmerald} !important`;
                }}
              >
                <FontAwesomeIcon icon={faLanguage} className={isArabic ? "ms-1" : "me-1"} />
                {isArabic ? 'ENGLISH' : 'العربية'}
              </button>
            </div>
            
            {/* Mobile Menu Button */}
            <div className="d-lg-none">
              <button 
                className="navbar-toggler-clean"
                onClick={handleShow}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  color: theme.darkEmerald,
                  padding: '0.5rem',
                  borderRadius: '8px',
                  transition: 'all 0.2s ease',
                  fontFamily: getFontFamily()
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = theme.hover;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                <FontAwesomeIcon icon={faBars} />
              </button>
            </div>
          </div>
        </Container>
      </Navbar>

      {/* Offcanvas menu for mobile */}
      <Offcanvas 
        show={showOffcanvas} 
        onHide={handleClose} 
        placement={isArabic ? "start" : "end"}
        className="offcanvas-clean"
        style={{ 
          maxWidth: '280px', 
          backgroundColor: 'white',
          direction: isArabic ? 'rtl' : 'ltr',
          fontFamily: getFontFamily()
        }}
      >
        <Offcanvas.Header 
          className="border-bottom"
          style={{ 
            borderBottom: `1px solid ${theme.border}`, 
            padding: '1.5rem',
            justifyContent: 'space-between'
          }}
        >
          <div className="d-flex align-items-center" style={{ margin: 0 }}>
            <img 
              src="/al-majd.png" 
              alt="مؤسسة إرضاء الإعلامية" 
              style={{
                width: '35px',
                height: '35px',
                objectFit: 'contain'
              }}
            />
            <div style={{ 
              fontWeight: '600', 
              color: theme.darkEmerald, 
              marginLeft: isArabic ? '0' : '8px', 
              marginRight: isArabic ? '8px' : '0',
              fontSize: '1.1rem',
              fontFamily: getFontFamily(600)
            }}>
              {translateText('AL-IRDA MEDIA')}
            </div>
          </div>
          
          <button 
            className="close-btn"
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              color: theme.darkEmerald,
              transition: 'all 0.2s ease',
              padding: '0.25rem',
              fontFamily: getFontFamily()
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = theme.hover;
              e.target.style.borderRadius = '8px';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </Offcanvas.Header>
        
        <Offcanvas.Body style={{ padding: '1rem 1.5rem' }}>
          <Nav className="flex-column">
            <LinkContainer to="/" onClick={handleClose}>
              <Nav.Link 
                className="nav-link-clean mb-2"
                style={{
                  color: `${theme.darkEmerald} !important`,
                  fontWeight: '500',
                  padding: '0.75rem 1rem',
                  borderRadius: '8px',
                  transition: 'all 0.2s ease',
                  textAlign: isArabic ? 'right' : 'left',
                  fontFamily: getFontFamily(500)
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = theme.hover;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                <FontAwesomeIcon icon={faHome} className={isArabic ? "ms-2" : "me-2"} />
                {translateText('Home')}
              </Nav.Link>
            </LinkContainer>
            
            <LinkContainer to="/news" onClick={handleClose}>
              <Nav.Link 
                className="nav-link-clean mb-2"
                style={{
                  color: `${theme.darkEmerald} !important`,
                  fontWeight: '500',
                  padding: '0.75rem 1rem',
                  borderRadius: '8px',
                  transition: 'all 0.2s ease',
                  textAlign: isArabic ? 'right' : 'left',
                  fontFamily: getFontFamily(500)
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = theme.hover;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                <FontAwesomeIcon icon={faNewspaper} className={isArabic ? "ms-2" : "me-2"} />
                {translateText('News')}
              </Nav.Link>
            </LinkContainer>

            {/* Language Toggle Button - Only in Sidebar */}
            <button 
              onClick={toggleLanguage}
              className="nav-link-clean mb-2"
              style={{
                background: 'none',
                border: `1px solid ${theme.darkEmerald}`,
                color: `${theme.darkEmerald} !important`,
                fontWeight: '500',
                padding: '0.75rem 1rem',
                borderRadius: '25px',
                transition: 'all 0.2s ease',
                textAlign: isArabic ? 'right' : 'left',
                width: '100%',
                cursor: 'pointer',
                fontFamily: getFontFamily(500),
                fontSize: '0.9rem',
                marginTop: '1rem'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = theme.darkEmerald;
                e.target.style.color = 'white !important';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = `${theme.darkEmerald} !important`;
              }}
            >
              <FontAwesomeIcon icon={faLanguage} className={isArabic ? "ms-2" : "me-2"} />
              {translateText('Switch Language')} ({isArabic ? 'ENGLISH' : 'العربية'})
            </button>
          </Nav>
        </Offcanvas.Body>
      </Offcanvas>

      <style jsx>{`
        .nav-link-clean {
          color: #255853 !important;
        }
        
        .nav-link-clean:hover, .nav-link-clean:focus {
          background-color: rgba(37, 88, 83, 0.1) !important;
          color: #255853 !important;
        }
        
        .close-btn:hover {
          background-color: rgba(37, 88, 83, 0.1) !important;
          border-radius: 8px;
        }
        
        .navbar-toggler-clean:hover {
          background-color: rgba(37, 88, 83, 0.1) !important;
        }
        
        /* Override Bootstrap default link colors */
        .navbar-nav .nav-link {
          color: #255853 !important;
        }
        
        .offcanvas-body .nav-link {
          color: #255853 !important;
        }
      `}</style>

      {/* Additional global styles to override Bootstrap */}
      <style jsx global>{`
        .navbar-light .navbar-nav .nav-link {
          color: #255853 !important;
        }
        
        .nav-link {
          color: #255853 !important;
        }
        
        .offcanvas-body .nav-link {
          color: #255853 !important;
        }
      `}</style>
    </>
  );
};

export default Header;
